/**
 * Change Notifications Hook
 * 
 * Tracks changes to the plan and shows toast notifications
 * when Q&A answers modify steps, time, or cost.
 * 
 * @see docs/project/specs/dynamic-template-system-v2-spec.md
 */

import { createElement, useCallback, useEffect, useRef, useState  } from 'react'
import { notifications } from '@mantine/notifications'
import { IconSparkles, IconX } from '@tabler/icons-react'
import {   computePlanDiff, generateDiffSummary } from '../utils'
import type {PlanDiff, PlanSnapshot} from '../utils';

interface UseChangeNotificationsOptions {
  templateSlug: string
  enabled?: boolean
}

interface UseChangeNotificationsResult {
  modifiedStepSlugs: Set<string>
  lastDiff: PlanDiff | null
  recordSnapshot: (snapshot: PlanSnapshot) => void
  compareAndNotify: (newSnapshot: PlanSnapshot) => PlanDiff | null
  clearModified: () => void
}

const TOAST_DURATION = 5000
const TOAST_ID_PREFIX = 'plan-change-'

/**
 * Hook to manage change notifications for plan modifications.
 * 
 * Usage:
 * 1. Call recordSnapshot() with initial plan state
 * 2. When user changes Q&A, call compareAndNotify() with new state
 * 3. Hook will show toast and track modified steps
 */
export function useChangeNotifications({
  templateSlug,
  enabled = true,
}: UseChangeNotificationsOptions): UseChangeNotificationsResult {
  const [modifiedStepSlugs, setModifiedStepSlugs] = useState<Set<string>>(new Set())
  const [lastDiff, setLastDiff] = useState<PlanDiff | null>(null)
  const previousSnapshot = useRef<PlanSnapshot | null>(null)
  const toastCounter = useRef(0)
  
  // Record a snapshot as the baseline for comparison
  const recordSnapshot = useCallback((snapshot: PlanSnapshot) => {
    previousSnapshot.current = snapshot
  }, [])
  
  // Compare new snapshot to previous and show notification if changed
  const compareAndNotify = useCallback((newSnapshot: PlanSnapshot): PlanDiff | null => {
    if (!previousSnapshot.current) {
      previousSnapshot.current = newSnapshot
      return null
    }
    
    const diff = computePlanDiff(previousSnapshot.current, newSnapshot)
    
    if (!diff.isEmpty && enabled) {
      setLastDiff(diff)
      
      // Accumulate modified slugs
      setModifiedStepSlugs(prev => {
        const next = new Set(prev)
        for (const slug of diff.modifiedStepSlugs) {
          next.add(slug)
        }
        return next
      })
      
      // Show toast notification
      const summary = generateDiffSummary(diff)
      const toastId = `${TOAST_ID_PREFIX}${++toastCounter.current}`
      
      notifications.show({
        id: toastId,
        title: 'Plan updated',
        message: summary,
        color: getMagnitudeColor(diff.magnitude),
        icon: createElement(IconSparkles, { size: 18 }),
        autoClose: TOAST_DURATION,
        withCloseButton: true,
      })
    }
    
    // Update baseline for next comparison
    previousSnapshot.current = newSnapshot
    
    return diff
  }, [enabled])
  
  // Clear all modified indicators
  const clearModified = useCallback(() => {
    setModifiedStepSlugs(new Set())
    setLastDiff(null)
  }, [])
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Dismiss any pending toasts from this component
      for (let i = 1; i <= toastCounter.current; i++) {
        notifications.hide(`${TOAST_ID_PREFIX}${i}`)
      }
    }
  }, [])
  
  return {
    modifiedStepSlugs,
    lastDiff,
    recordSnapshot,
    compareAndNotify,
    clearModified,
  }
}

function getMagnitudeColor(magnitude: PlanDiff['magnitude']): string {
  switch (magnitude) {
    case 'major':
      return 'blue'
    case 'moderate':
      return 'cyan'
    case 'minor':
      return 'gray'
    default:
      return 'gray'
  }
}
